import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactImageAnnotate from "@idapgroup/react-image-annotate";
import Swal from "sweetalert2";
import {
  Button,
  CircularProgress,
  Alert,
  Drawer,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
  Badge
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import "./AnnotatePage.css";
import { FixedSizeList } from 'react-window';

const AnnotatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { imageDict, system_name, outflowId } = location.state || {
    imageIds: [],
    system_name: "",
  };
  const [images, setImages] = useState<{ src: string; name: string; regions?: any }[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<number[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const totalImages = imageDict.length;

  useEffect(() => {
    const fetchImages = async () => {
      if (imageDict.length === 0) {
        setError("No images to annotate.");
        return;
      }

      setLoading(true);
      setError(null);
      setFailedImageIds([]);

      const fetchedImages = await Promise.all(
        imageDict.map(async (image: any) => {
          try {
            // Pass system_name to fetch both user annotations and system detections
            const defectsResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/image/${image.media_id}/defects?system_name=${encodeURIComponent(system_name)}`
            );

            if (!defectsResponse.ok) {
              const defectsErrorData = await defectsResponse.json();
              const defectsErrorDetail = defectsErrorData.detail || "Failed to fetch defects.";
              console.error(`Error fetching defects for image ID ${image.id}: ${defectsErrorDetail}`);
              setFailedImageIds((prev) => [...prev, image.id]);
              return null;
            }
            console.log(image);
            const defects = await defectsResponse.json();
            const defect_data = defects.defect_data;

            // Enhance system detections to display disposition information
            const enhancedDefects = defect_data.map((defect: any) => {
              if (defect.source === 'system' && defect.disposition) {
                return {
                  ...defect,
                  cls: `System: ${defect.disposition}`,  // Show disposition in the class label
                  comment: `Disposition: ${defect.disposition}\nConfidence: ${(defect.confidence * 100).toFixed(1)}%`,
                  editingLabels: false  // Disable label editing for system detections
                };
              }
              return defect;
            });

            console.log("Enhanced defect data with system detections:", enhancedDefects);
            setLoadedCount((prev) => prev + 1);
            return {
              src: image.path,
              thumbnailSrc: image.path,
              name: `Image ${image.media_id}`,
              regions: enhancedDefects,
            };
          } catch (err) {
            console.error(`Unexpected error fetching image ID ${image.id}:`, err);
            setFailedImageIds((prev) => [...prev, image.id]);
            return null;
          }
        })
      );
      const successfulImages = fetchedImages.filter((img) => img !== null) as {
        src: string;
        name: string;
        regions?: any;
      }[];

      setImages(successfulImages);
      setLoading(false);

      if (successfulImages.length === 0) {
        setError("All images failed to load.");
      } else if (failedImageIds.length > 0) {
        setError(`${failedImageIds.length} image(s) failed to load.`);
      }
    };

    fetchImages();
  }, [imageDict, system_name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      images.forEach((image) => {
        URL.revokeObjectURL(image.src);
      });
    };
  }, [images]);

  const handleSaveImage = () => {
    // Save the annotated image
  };

  const handleNextImage = () => {
    handleSaveImage();
    setCurrentImageIndex((prevIndex) =>
      prevIndex < images.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  const handlePreviousImage = () => {
    handleSaveImage();
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleExit = async (output: any) => {
    try {
      Swal.fire({
        title: 'Saving Annotations',
        text: 'Please wait while your annotations are being saved...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      // Save annotations for the current image
      const imageId = imageDict[currentImageIndex].id;
      const allRegions = output.images[currentImageIndex].regions;

      // Filter out system detections - only save user annotations
      const userAnnotations = allRegions.filter((region: any) =>
        region.source !== 'system' && !String(region.id).startsWith('system_')
      );

      const imageMetadata = imageDict[currentImageIndex];
      console.log("Saving only user annotations:", userAnnotations);
      console.log(imageMetadata);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/save_annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_id: imageId,
          system_name: system_name,
          annotations: userAnnotations,
          metadata: imageMetadata,
          outflowId: outflowId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to save annotations:', errorData);
        throw new Error(errorData.detail || 'Failed to save annotations');
      }

      const temp = images;
      temp[currentImageIndex].regions = allRegions; // Keep all regions including system detections in UI
      setImages(temp);
      console.log('IMAGES:', images);

      Swal.fire({
        title: "Annotations Saved",
        text: "Your annotations have been saved successfully.",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        console.log("Annotated data:", output);
      });
    } catch (error) {
      console.error('Error saving annotations:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to save annotations. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleRetry = async () => {
    if (failedImageIds.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    const retriedImages = await Promise.all(
      failedImageIds.map(async (imageId: number) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/fetch_image/${imageId}?draw_defects=false&retrieval_method=remote&system_name=${encodeURIComponent(system_name)}`
          );

          if (!response.ok) {
            const errorData = await response.json();
            const errorDetail = errorData.detail || "Failed to fetch image.";
            console.error(`Error fetching image ID ${imageId}: ${errorDetail}`);
            return null;
          }

          const blob = await response.blob();
          const defectsResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/image_defects/${imageId}?system_name=${encodeURIComponent(system_name)}`
          );

          if (!defectsResponse.ok) {
            const defectsErrorData = await defectsResponse.json();
            const defectsErrorDetail = defectsErrorData.detail || "Failed to fetch defects.";
            console.error(`Error fetching defects for image ID ${imageId}: ${defectsErrorDetail}`);
            return null;
          }

          const defects = await defectsResponse.json();
          const defect_data = defects.defect_data;

          return {
            src: URL.createObjectURL(blob),
            thumbnailSrc: URL.createObjectURL(blob),
            name: `Image ${imageId}`,
            regions: defect_data,
          };
        } catch (err) {
          console.error(`Unexpected error fetching image ID ${imageId}:`, err);
          return null;
        }
      })
    );

    const successfulRetries = retriedImages.filter((img) => img !== null) as {
      src: string;
      name: string;
      regions?: any;
    }[];

    setImages((prevImages) => [...prevImages, ...successfulRetries]);
    setFailedImageIds(
      failedImageIds.filter(
        (id) => !retriedImages.some((img) => img && img.name === `Image ${id}`)
      )
    );
    setLoading(false);

    if (successfulRetries.length === 0) {
      setError("Retry failed. All images are still not loading.");
    } else if (failedImageIds.length > 0) {
      setError(`${failedImageIds.length} image(s) still failed to load.`);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Create metadata list for sidebar
  // Create a separate memoized sidebar component
  // Create a separate memoized sidebar component
  const SidebarContent = useMemo(() => {
    return React.memo(({ 
      imageDict, 
      currentImageIndex, 
      setCurrentImageIndex, 
      totalImages 
    }: {
      imageDict: any[];
      currentImageIndex: number;
      setCurrentImageIndex: (index: number) => void;
      totalImages: number;
    }) => {
      return (
        <>
          <Box sx={{ 
            padding: '16px', 
            backgroundColor: '#1976d2', 
            color: 'white',
            marginBottom: '8px' // Add spacing after header
          }}>
            <Typography variant="h6">Image Metadata</Typography>
            <Typography variant="subtitle2">Total Images: {totalImages}</Typography>
          </Box>
          
          <FixedSizeList
            height={window.innerHeight - 120}
            width={300}
            itemSize={150} // Increased from 85 to allow more space
            itemCount={imageDict.length}
            overscanCount={5}
            style={{ overflowX: 'hidden' }} // Prevent horizontal scrollbar
          >
            {({ index, style }) => {
              const image = imageDict[index];
              return (
                <div style={{
                  ...style,
                  width: '100%', // Ensure the item doesn't exceed container width
                }}>
                  <Box
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{ 
                      width: '100%',
                      padding: '12px 16px', // Increased padding
                      backgroundColor: currentImageIndex === index ? '#bbdefb' : 'inherit',
                      '&:hover': { backgroundColor: '#e3f2fd' },
                      cursor: 'pointer',
                      boxSizing: 'border-box', // Ensure padding is included in width
                    }}
                  >
                    <ListItemText
                      primary={`Image ${index + 1}: ${image.media_id}`}
                      secondary={
                        <React.Fragment>
                          <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.primary"
                            sx={{ display: 'block', marginTop: '4px' }} // Add space and make it block
                          >
                            {image.timestamp}
                          </Typography>
                          <Typography 
                            component="span" 
                            variant="body2" 
                            color="text.primary"
                            sx={{ display: 'block', marginTop: '4px' }} // Add space and make it block
                          >
                            {image.camera}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </Box>
                  <Divider />
                </div>
              );
            }}
          </FixedSizeList>
        </>
      );
    });
  }, []);

  if (loading && images.length === 0) {
    return (
      <div className="AnnotatePageContainer" style={{ height: "85vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={60} />
        <p style={{ marginLeft: "10px" }}>Loading {loadedCount} of {totalImages} images...</p>
      </div>
    );
  }

  return (
    <div className="AnnotatePageContainer" style={{ height: "85vh" }}>
      {/* Header with image count and sidebar toggle */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '8px 16px', 
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        zIndex: 1000
      }}>
        <IconButton 
          onClick={toggleSidebar} 
          color="primary" 
          aria-label="open metadata sidebar"
        >
          <MenuIcon />
        </IconButton>
        
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, textAlign: 'center' }}>
          <Badge  color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '1rem', height: '22px', minWidth: '22px' } }}>
            Image {currentImageIndex + 1} of {totalImages}
          </Badge>
        </Typography>
        
        <Button 
          variant="outlined" 
          color="primary" 
          size="small"
          onClick={() => navigate("/teachmode")}
        >
          Exit
        </Button>
      </Box>

      {/* Metadata Sidebar */}
      <Drawer
  anchor="left"
  open={sidebarOpen}
  onClose={toggleSidebar}
  sx={{ 
    '& .MuiDrawer-paper': { 
      width: '300px',
      boxSizing: 'border-box',
      overflowX: 'hidden' // Prevent horizontal scrollbar at drawer level
    }
  }}
>
  {sidebarOpen && (
    <SidebarContent 
      imageDict={imageDict}
      currentImageIndex={currentImageIndex}
      setCurrentImageIndex={setCurrentImageIndex}
      totalImages={totalImages}
    />
  )}
</Drawer>

      {loading && (
        <div className="loading-overlay">
          <CircularProgress size={60} />
          <p>Loading {loadedCount} of {totalImages} images...</p>
        </div>
      )}

      {error && (
        <div className="error-message" style={{ padding: "20px", textAlign: "center" }}>
          <Alert severity="error">{error}</Alert>
          {failedImageIds.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRetry}
              style={{ marginTop: '10px' }}
            >
              Retry Loading Failed Images
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/teachmode")}
            style={{ marginTop: '10px', marginLeft: '10px' }}
          >
            Go Back
          </Button>
        </div>
      )}

      {images.length > 0 && !loading && (
        <div style={{ position: 'relative', height: 'calc(100% - 56px)' }}>
          <ReactImageAnnotate
            key={currentImageIndex}
            images={images}
            allowComments={true}
            enabledTools={["select","pan","zoom","create-box","show-mask"]}
            taskDescription={
              imageDict[currentImageIndex]
                ? `Timestamp: ${imageDict[currentImageIndex].timestamp}
                   Media ID: ${imageDict[currentImageIndex].media_id}
                   Camera: ${imageDict[currentImageIndex].camera}
                   Disposition: ${imageDict[currentImageIndex].disposition || 'N/A'}
                   Model: ${imageDict[currentImageIndex].model}
                   Part Name: ${imageDict[currentImageIndex].part_name}
                   Part Number: ${imageDict[currentImageIndex].part_number}

                   🔶 Gold boxes = System detections (locked)
                   🔵 Blue boxes = User annotations (editable)`
                : "No metadata available"
            }
            selectedImage={currentImageIndex}
            regionClsList={["Split"]}
            hideClone={true}
            onExit={handleExit}
            onNextImage={(output) => {
              handleNextImage();
              console.log("Navigating to the next image:", output);
            }}
            onPrevImage={(output) => {
              handlePreviousImage();
              console.log("Navigating to the previous image:", output);
            }}
          />
        </div>
      )}

      {!loading && images.length === 0 && !error && (
        <div className="no-images-message" style={{ padding: "20px", textAlign: "center" }}>
          <p>No images available for annotation.</p>
          <Button variant="contained" color="primary" onClick={() => navigate("/teachmode")}>
            Go Back
          </Button>
        </div>
      )}
    </div>
  );
};

export default AnnotatePage;